import {
  PHONE_COUNTRY_OPTIONS,
  PHONE_DIAL_CODES_DESC,
  dialCodeForRegion,
} from '../constants/phoneCountryCodes';
import { getLocaleFlag } from '../constants/appStoreLocalizations';

const LANG_TO_REGION: Record<string, string> = {
  tr: 'TR',
  en: 'US',
  de: 'DE',
  fr: 'FR',
  es: 'ES',
  it: 'IT',
  pt: 'PT',
  nl: 'NL',
  pl: 'PL',
  ru: 'RU',
  ja: 'JP',
  ko: 'KR',
  zh: 'CN',
  ar: 'SA',
  he: 'IL',
  hi: 'IN',
  sv: 'SE',
  da: 'DK',
  fi: 'FI',
  no: 'NO',
  cs: 'CZ',
  sk: 'SK',
  ro: 'RO',
  hu: 'HU',
  el: 'GR',
  uk: 'UA',
  vi: 'VN',
  th: 'TH',
  id: 'ID',
  ms: 'MY',
};

function regionFromLocale(locale: string): string | null {
  const normalized = locale.trim().replace('_', '-');
  if (!normalized) return null;
  const parts = normalized.split('-');
  const last = parts[parts.length - 1];
  if (last && /^[A-Za-z]{2}$/.test(last) && parts.length >= 2) {
    return last.toUpperCase();
  }
  return LANG_TO_REGION[parts[0].toLowerCase()] ?? null;
}

/** Best-effort region from browser / i18n locale (e.g. tr-TR → TR). */
export function detectBrowserRegion(preferredLocale?: string): string {
  if (typeof navigator === 'undefined') {
    return regionFromLocale(preferredLocale ?? '') ?? 'US';
  }
  const candidates = [
    preferredLocale,
    navigator.language,
    ...(navigator.languages ?? []),
  ].filter(Boolean) as string[];
  for (const locale of candidates) {
    const region = regionFromLocale(locale);
    if (region && PHONE_COUNTRY_OPTIONS.some(c => c.region === region)) {
      return region;
    }
  }
  return 'US';
}

export function getDefaultPhoneDialCode(preferredLocale?: string): string {
  return dialCodeForRegion(detectBrowserRegion(preferredLocale));
}

export function getRegionFlag(region: string): string {
  return getLocaleFlag(`en-${region.toUpperCase()}`);
}

export function splitE164Phone(
  phone: string,
  fallbackDialCode: string
): { dialCode: string; localNumber: string } {
  const trimmed = phone.trim();
  if (!trimmed) {
    return { dialCode: fallbackDialCode, localNumber: '' };
  }
  const compact = trimmed.replace(/[^\d+]/g, '');
  if (!compact.startsWith('+')) {
    const local = compact.replace(/\D/g, '').replace(/^0+/, '');
    return { dialCode: fallbackDialCode, localNumber: local };
  }
  for (const dial of PHONE_DIAL_CODES_DESC) {
    if (compact.startsWith(dial)) {
      const local = compact.slice(dial.length).replace(/\D/g, '');
      return { dialCode: dial, localNumber: local };
    }
  }
  return { dialCode: fallbackDialCode, localNumber: compact.slice(1) };
}

export function combineE164Phone(dialCode: string, localNumber: string): string {
  let local = localNumber.replace(/\D/g, '');
  if (!local) return '';
  if (local.startsWith('0')) {
    local = local.replace(/^0+/, '');
  }
  if (!local) return '';
  const dial = dialCode.startsWith('+') ? dialCode : `+${dialCode}`;
  return `${dial}${local}`;
}
